import { useEffect } from 'react';

type Action = Record<string, unknown>;

/**
 * Synchronyze two different React applications that use contexts and
 * dispatchers to change state.
 *
 * The local and external app names are the application names you want to
 * sync together, and dispatch is the dispatcher function of the external
 * app. When the external app calls sync(action), this will trigger also
 * in the local dispatcher.
 *
 * On the other hand, this hook will return a sync() function, so you
 * can dispatch actions to the remote dispatcher too.
 *
 * Example:
 *
 *   App1.tsx
 *     // Automatically listen to App2 syncs
 *     const sync = useAppSync('App2', App1', dispatch);
 *
 *     // Dispatch locally and in App2 too.
 *     dispatch(action);
 *     sync(action);
 *
 *
 *   App2.tsx
 *     // Automatically listen to App1 syncs
 *     const sync = useAppSync('App1', 'App2', dispatch);
 *
 *     // Dispatch locally and in App1 too.
 *     dispatch(action);
 *     sync(action);
 */
function useAppSync(
  externalAppName: string,
  localAppName: string,
  dispatch: (action: Action) => void,
): typeof sync {
  useEffect(() => {
    const listener = (e) => dispatch((e as CustomEvent).detail);

    window.addEventListener(localAppName, listener);
    return () => window.removeEventListener(localAppName, listener);
  }, [dispatch, localAppName]);

  const sync = (action: Action) => window.dispatchEvent(
    new CustomEvent(externalAppName, { detail: action }),
  );

  return sync;
}

export default useAppSync;
